"use client";
import { BlockBreadcrumbsV2 } from "@/app/_blocks/BlockBreadcrumbsv2";
import { Button } from "@ui/components/core/Button";
import { cn } from "@ui/lib/utils";
import ArrowUp from "@ui/icons/arrow_up.svg";
import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import {
  MAX_UPLOAD_DURATION_MINUTES,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  useHeroSubtitleMedia,
} from "./useHeroSubtitleMedia";
import { Check, Trash } from "lucide-react";
import { CategoryCardBody, CategoryCardRoot } from "@ui/components/core/CategoryCard";
import { Media } from "@mw/shared/payload-types";
import { LimitReachedOverlay } from "@ui/components/demos/LimitReachedOverlay";
import { useHeroSubtitleMediaTranslations } from "./HeroSubtitleMediaTranslations";
import FakeProgressBar from "@ui/components/core/FakeProgressBar";
import { LanguageOrAccent, languages as languagesAndAccents } from "@mw/shared/languagesAndAccents";
import { LanguageSelector as CoreLanguageSelector } from "@ui/components/core/LanguageSelector";
import type { LanguageMetadata } from "@ui/components/languages/config";
import { EventPropagationBoundary } from "@ui/components/composers/EventPropagationBoundary";
import { SelectTrigger } from "@ui/components/core/Select";
import clsx from "clsx";
import { Subtitles } from "@utilities/subtitles/subtitleTypes";
import { BoxedAspectRatioVideoPlayer } from "@ui/components/core/BoxedAspectRatioVideo/BoxedAspectRatioVideoPlayer";
import { toast } from "@/app/_providers/toast";
import { EventName } from "@mw/shared/analyticsEventNames";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { appUrls } from "@/constants";
import { logger } from "@utilities/logger";

export interface HeroSubtitleMediaClientDemoProps {
  breadcrumbs?: { href?: string; title: string }[];
  title?: null | string;
  initialSubtitleLanguage?: string;
  media: {
    demoUrl: string;
    demoSubtitles?: Subtitles;
  };
}

const subtitleLanguages = languagesAndAccents.filter(l => l.asr).sort((a, b) => a.label.localeCompare(b.label));

export default function HeroSubtitleMediaClientDemo({
  breadcrumbs,
  title,
  initialSubtitleLanguage,
  media,
}: HeroSubtitleMediaClientDemoProps) {
  const t = useHeroSubtitleMediaTranslations();
  const [selectedSubtitleLanguage, setSelectedSubtitleLanguage] = useState<LanguageOrAccent | undefined>(
    initialSubtitleLanguage ? subtitleLanguages.find(l => l.value === initialSubtitleLanguage) : undefined,
  );

  const { uploadVideo, generate, state, reset } = useHeroSubtitleMedia({
    demoVideo: {
      videoUrl: media.demoUrl,
    },
    demoSubtitles: media.demoSubtitles,
  });

  const currentVideo = state.video;
  const currentSubtitles = state.subtitles;

  const selectedFile = state.type === "uploaded" || state.type === "generating" ? state.file : undefined;
  const fileThumbnail = state.type === "uploaded" || state.type === "generating" ? state.fileThumbnail : undefined;

  return (
    <>
      {/* TOP */}
      <div className='gap-24 space-y-24 lg:flex lg:space-y-0'>
        {/* Left */}
        <div className='flex flex-1 flex-col justify-between space-y-24 pr-10 lg:space-y-0'>
          <div className=''>
            {breadcrumbs && <BlockBreadcrumbsV2 links={breadcrumbs} size='small' hidenOnMobileAfter={0} />}
            <h1 className='f-heading-03 max-w-3xl lg:f-heading-02'>{title}</h1>
          </div>

          {state.type === "complete" ? (
            <CompleteCard
              fileThumbnail={state.fileThumbnail}
              url={`${appUrls.speechToText}?claimId=${state.subtitleId}`}
              subtitleLanguage={selectedSubtitleLanguage}
            />
          ) : (
            <VideoSubtitleUploadDropzone
              onFilesDropped={files => {
                if (files.length === 0) return;
                uploadVideo(files[0]);
              }}
              generate={() => generate(selectedSubtitleLanguage)}
              isGenerating={state.type === "generating"}
              clearFiles={reset}
              selectedFile={selectedFile}
              fileThumbnail={fileThumbnail}
              subtitleLanguage={selectedSubtitleLanguage}
              onSubtitleLanguageChange={val =>
                setSelectedSubtitleLanguage(subtitleLanguages.find(l => l.value === val))
              }
              subtitleLanguages={subtitleLanguages}
              onError={error => {
                logger.error(new Error(error), "Error in VideoSubtitleUploadDropzone");
                toast.error(error);
              }}
            />
          )}
        </div>

        {/* Right */}
        <div className='flex-1'>
          <BoxedAspectRatioVideoPlayer src={currentVideo.videoUrl} subtitles={currentSubtitles} />
        </div>
      </div>

      {state.type === "quota_exceeded" && (
        <div className='absolute inset-0 overflow-hidden rounded-xxl'>
          <LimitReachedOverlay
            title={t("widget.quota_exceeded.title")}
            description={t("widget.quota_exceeded.description")}
            onClose={reset}
            trackingConfig={{
              signUpLink: {
                eventName: EventName.mw_subtitle_media_limit_sign_up_clicked,
                eventProperties: {
                  limit_type: "quota",
                },
              },
              closeButton: {
                eventName: EventName.mw_subtitle_media_limit_closed,
                eventProperties: {
                  limit_type: "quota",
                },
              },
            }}
          />
        </div>
      )}
      {state.type === "duration_exceeded" && (
        <div className='absolute inset-0 overflow-hidden rounded-xxl'>
          <LimitReachedOverlay
            title={t("widget.duration_exceeded.title")}
            description={t("widget.duration_exceeded.description", { maxDuration: MAX_UPLOAD_DURATION_MINUTES })}
            buttonText={t("widget.duration_exceeded.button")}
            onClose={reset}
            trackingConfig={{
              signUpLink: {
                eventName: EventName.mw_subtitle_media_limit_sign_up_clicked,
                eventProperties: {
                  limit_type: "duration",
                  max_duration_minutes: MAX_UPLOAD_DURATION_MINUTES,
                },
              },
              closeButton: {
                eventName: EventName.mw_subtitle_media_limit_closed,
                eventProperties: {
                  limit_type: "duration",
                  max_duration_minutes: MAX_UPLOAD_DURATION_MINUTES,
                },
              },
            }}
          />
        </div>
      )}
    </>
  );
}

const MEDIA_CONTENT_TYPES = ["video/*"];

export interface VideoSubtitleUploadDropzoneProps {
  onFilesDropped: (files: File[]) => void;
  onError?: (error: string) => void;
  maxSize?: number; // in bytes
  selectedFile?: File;
  fileThumbnail?: string;
  clearFiles: () => void;
  isGenerating?: boolean;
  generate: () => Promise<void>;
  // language select
  subtitleLanguage: LanguageOrAccent | undefined;
  onSubtitleLanguageChange: (val: string) => void;
  subtitleLanguages: LanguageOrAccent[];
}

export function VideoSubtitleUploadDropzone({
  onFilesDropped,
  onError,
  maxSize = MAX_UPLOAD_SIZE_BYTES,
  selectedFile,
  fileThumbnail,
  clearFiles,
  isGenerating,
  generate,
  subtitleLanguage,
  onSubtitleLanguageChange,
  subtitleLanguages,
}: VideoSubtitleUploadDropzoneProps) {
  const t = useHeroSubtitleMediaTranslations();
  const handleDrop = useCallback(
    (files: File[]) => {
      onFilesDropped(files);
    },
    [onFilesDropped],
  );

  const handleDropRejected = useCallback(
    (rejected: FileRejection[]) => {
      if (rejected.length === 0) return;
      const { file, errors } = rejected[0];
      let errorMessage = t("widget.file_validation.general_error", { file: file.name });

      if (errors.some(e => e.code === "file-invalid-type")) {
        errorMessage = t("widget.file_validation.invalid_type", { file: file.name });
      } else if (errors.some(e => e.code === "file-too-large")) {
        errorMessage = t("widget.file_validation.too_large", { file: file.name, maxSize: MAX_UPLOAD_SIZE_MB });
      }

      if (onError) {
        onError(errorMessage);
      }
    },
    [onError, t],
  );

  const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    accept: Object.fromEntries(MEDIA_CONTENT_TYPES.map(type => [type, []])),
    maxSize,
    onDropAccepted: handleDrop,
    onDropRejected: handleDropRejected,
    noClick: true,
    noKeyboard: true,
    disabled: isGenerating,
  });

  const selectedLanguage = subtitleLanguages.find(l => l.value === subtitleLanguage?.value);

  const hasFile = !!selectedFile;

  const rootProps = getRootProps({
    className: cn(
      "relative rounded-inxl border shadow-lg px-24 py-24 transition-colors",
      !hasFile && "cursor-pointer",
      isDragActive && !hasFile && "border-primary bg-neutral-dark",
      isGenerating && "pointer-events-none",
    ),
  });

  const interactiveProps = hasFile
    ? {}
    : {
        "aria-label": t("widget.dropzone.title"),
        role: "button" as const,
        tabIndex: 0,
        onClick: open,
      };

  return (
    <div {...rootProps} {...interactiveProps}>
      <input {...getInputProps()} className='hidden' />

      {!selectedFile && (
        <>
          <div className='mb-24 flex justify-between'>
            <div className=''>
              <p className='f-paragraph-02 mb-8'>
                {subtitleLanguage
                  ? t("widget.dropzone.title_with_language", {
                      language: subtitleLanguage.label,
                    })
                  : t("widget.dropzone.title")}
              </p>
              <p className='f-paragraph-04 text-sm'>
                {t("widget.dropzone.subtitle", {
                  maxSize: MAX_UPLOAD_SIZE_MB,
                  maxDuration: MAX_UPLOAD_DURATION_MINUTES,
                })}
              </p>
            </div>

            {/* Upload Icon */}
            <div className='bg-transparent ml-8 cursor-pointer self-start rounded-md border p-8 shadow-lg transition-colors lg:p-12'>
              <ArrowUp className='text-primary size-20 lg:size-24' />
            </div>
          </div>
          <div className='max-w-2xl'>
            {/* Select button */}
            <Button
              onClick={e => {
                e.stopPropagation();
                open();
              }}
              disabled={isGenerating}
              size='lg'
            >
              {t("widget.buttons.select_media")}
            </Button>
          </div>
        </>
      )}
      {selectedFile && (
        <>
          <CategoryCardRoot
            variant='outline'
            onClick={e => {
              e.stopPropagation();
            }}
            className={clsx(isGenerating && "opacity-50")}
          >
            <CategoryCardBody
              title={selectedFile.name}
              description={
                <>
                  <span>{(selectedFile.size / (1000 * 1000)).toFixed(2)} MB</span>
                  <span>·</span>
                  <span>{selectedFile.name.split(".").pop()}</span>
                </>
              }
              imageProps={{
                resource: {
                  alt: selectedFile.name,
                  url: fileThumbnail || "",
                  width: 100,
                  height: 100,
                } as Media,
              }}
            />

            <Button variant='ghost' size='sm' onClick={clearFiles} disabled={isGenerating}>
              <Trash />
            </Button>
          </CategoryCardRoot>
          {isGenerating && (
            <div className='pb-12 pt-24'>
              <p className='f-paragraph-02 mb-12 text-default'>{t("widget.generation_in_progress")}</p>
              <FakeProgressBar duration={MAX_UPLOAD_DURATION_MINUTES * 30} isComplete={false} className='mx-0 h-8' />
            </div>
          )}
          {/* Language select */}
          {!isGenerating && (
            <EventPropagationBoundary className='max-w-2xl pt-24'>
              <div>
                <p className='f-paragraph-04 mb-6'>{t("widget.language_select.subtitle_language_label")}</p>
                <CoreLanguageSelector
                  fullWidth
                  language={selectedLanguage as unknown as LanguageMetadata | undefined}
                  setSelectedLanguage={onSubtitleLanguageChange}
                  showDropdownArrow
                  sortedLanguages={subtitleLanguages as unknown as LanguageMetadata[]}
                  triggerSize='md'
                  customSelectTrigger={({ language }) => (
                    <SelectTrigger className='w-full' size='md'>
                      {language ? language.label : t("widget.language_select.subtitle_language_placeholder")}
                    </SelectTrigger>
                  )}
                />
              </div>
            </EventPropagationBoundary>
          )}
          {!isGenerating && (
            <div className='max-w-2xl pt-20'>
              {/* Generate button */}
              <Button
                onClick={e => {
                  e.stopPropagation();
                  generate();
                }}
                size='lg'
              >
                <span>{t("widget.buttons.generate_subtitles")}</span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CompleteCard({
  fileThumbnail,
  url,
  subtitleLanguage,
}: {
  fileThumbnail?: string;
  url: string;
  subtitleLanguage?: LanguageOrAccent;
}) {
  const t = useHeroSubtitleMediaTranslations();

  return (
    <div className='relative rounded-inxl border bg-dark px-24 py-24 shadow-lg transition-colors'>
      <div className='flex flex-col items-center justify-center gap-16'>
        {/* Thumbnail with check */}
        <div className='relative'>
          <div className='h-72 w-72 overflow-hidden rounded-lg bg-overlay-light'>
            {fileThumbnail ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={t("widget.complete.thumbnail_alt")}
                className='h-full w-full object-cover'
                src={fileThumbnail}
              />
            ) : null}
          </div>
          <div className='absolute -bottom-12 -right-12 rounded-sm border border-default bg-default p-4 shadow-xs'>
            <Check className='size-20 text-default' />
          </div>
        </div>

        {/* Text */}
        <div className='pt-8'>
          <h3 className='f-heading-05 text-center'>{t("widget.complete.title")}</h3>
          <p className='f-paragraph-03 mt-6 max-w-sm text-center text-lighter'>{t("widget.complete.description")}</p>
        </div>

        <div className='mt-24 flex flex-col items-center justify-center gap-12 md:flex-row'>
          <LinkTracked
            href={url}
            asButton
            buttonVariants={{
              size: "lg",
              variant: "primary",
            }}
            eventName={EventName.mw_subtitle_media_download_clicked}
            eventProperties={{
              subtitle_lang: subtitleLanguage?.value || "auto",
            }}
          >
            {t("widget.complete.download")}
          </LinkTracked>
        </div>
      </div>
    </div>
  );
}
